import React from 'react';
import { ArrowsClockwise } from '@phosphor-icons/react';
import { isSameDay } from '../../utils/sameDay';

/**
 * DivisionModal — configure divisions + rollover behavior for a single class.
 *
 * Rollover semantics flipped to "Rollover TO": when editing class X, the
 * producer ticks the destination classes that X can roll into. Internally we
 * still store `rollover_from_class_ids` on each destination, but the UX is
 * easier to reason about ("class X rolls into A, B, C").
 */
export const DivisionModal = ({
  show, configuringClass, divisionType, divisionSplit,
  onSetDivisionType, onSetDivisionSplit, onSave, onCancel, onSetRolloverFrom
}) => {
  if (!configuringClass) return null;

  const targetDay = configuringClass.scheduled_date || configuringClass.scheduled_day || '';
  const sourceId = configuringClass.id;

  // Eligible "Roll over TO" destinations: any same-day class other than
  // the one being configured. (Same-day rule mirrors the rider flow.)
  const eligibleDestinations = (show.classes || []).filter((c) => {
    if (c.id === sourceId) return false;
    return isSameDay(configuringClass, c);
  });

  // For destination D, "checked" means D's rollover_from_class_ids already
  // contains the configured class's id.
  const isDestChecked = (dest) => {
    const fromIds = dest.rollover_from_class_ids?.length
      ? dest.rollover_from_class_ids
      : (dest.rollover_from_class_id ? [dest.rollover_from_class_id] : []);
    return fromIds.includes(sourceId);
  };

  // Toggling a destination updates THAT destination's rollover_from_class_ids
  // (NOT the configured class's).
  const toggleDestination = (dest) => {
    const fromIds = dest.rollover_from_class_ids?.length
      ? dest.rollover_from_class_ids
      : (dest.rollover_from_class_id ? [dest.rollover_from_class_id] : []);
    const next = fromIds.includes(sourceId)
      ? fromIds.filter((id) => id !== sourceId)
      : [...fromIds, sourceId];
    onSetRolloverFrom(dest.id, next, dest.rollover_category);
  };

  // Anyone currently rolling INTO the configured class — surfaced as a small
  // read-only summary so producers don't lose sight of inbound rollovers when
  // they flip to the source-side picker.
  const inboundFrom = (configuringClass.rollover_from_class_ids?.length
    ? configuringClass.rollover_from_class_ids
    : (configuringClass.rollover_from_class_id ? [configuringClass.rollover_from_class_id] : [])
  )
    .map((id) => (show.classes || []).find((c) => c.id === id))
    .filter(Boolean);

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#161616] border border-[#0096FF] rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit' }}>Configure Divisions</h3>
        <p className="text-[#9E9E9E] mb-6">{configuringClass.name}</p>
        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Division Type</label>
            <select
              value={divisionType}
              onChange={(e) => onSetDivisionType(e.target.value)}
              className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors"
              data-testid="division-type-select"
            >
              <option value="NONE">Not Set (Decide Later)</option>
              <option value="4D">4D (0.5s, 1.0s, 2.0s splits)</option>
              <option value="3D">3D (1.0s, 2.0s splits)</option>
              <option value="2D">2D (Custom split)</option>
              <option value="OVERALL">No Divisions (Overall Only)</option>
            </select>
          </div>
          {divisionType === '2D' && (
            <div>
              <label className="block text-sm font-medium text-[#F8F8F8] mb-2">2D Split Time</label>
              <select
                value={divisionSplit}
                onChange={(e) => onSetDivisionSplit(parseFloat(e.target.value))}
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF] transition-colors"
                data-testid="division-split-select"
              >
                <option value="0.5">0.5 seconds</option>
                <option value="1.0">1.0 seconds</option>
              </select>
            </div>
          )}

          <div data-testid="rollover-to-section">
            <label className="block text-sm font-medium text-[#FF9500] mb-2 flex items-center gap-1.5">
              <ArrowsClockwise size={14} weight="bold" /> Rollover To Classes
            </label>
            <p className="text-xs text-[#9E9E9E] mb-2">
              Pick the classes that <span className="text-white font-semibold">{configuringClass.name}</span> can roll into.
              Same-day only — Friday classes can&apos;t roll into Saturday and vice versa.
            </p>
            <div className="space-y-1.5">
              {eligibleDestinations.length === 0 ? (
                <p className="text-xs text-[#9E9E9E] italic px-2 py-3" data-testid="no-same-day-rollover-classes">
                  No other classes scheduled on {targetDay || 'this day'} yet.
                </p>
              ) : (
                eligibleDestinations.map((dest) => {
                  const checked = isDestChecked(dest);
                  return (
                    <label
                      key={dest.id}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-colors ${
                        checked ? 'bg-[#FF9500]/10 border border-[#FF9500]/30' : 'bg-[#0A0A0A] border border-[#262626] hover:border-[#FF9500]/20'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleDestination(dest)}
                        className="accent-[#FF9500]"
                        data-testid={`rollover-to-check-${dest.id}`}
                      />
                      <span className="text-white text-sm flex-1">{dest.name}</span>
                      {dest.rollover_category && (
                        <span className="px-1.5 py-0.5 bg-[#FF9500]/15 text-[#FF9500] rounded text-[9px] font-bold uppercase">
                          {dest.rollover_category}
                        </span>
                      )}
                    </label>
                  );
                })
              )}
            </div>
          </div>

          {inboundFrom.length > 0 && (
            <div className="border-t border-[#262626] pt-3" data-testid="rollover-inbound-summary">
              <p className="text-[10px] uppercase tracking-wider text-[#9E9E9E] font-semibold mb-1.5">
                Currently rolling INTO this class
              </p>
              <div className="flex flex-wrap gap-1.5">
                {inboundFrom.map((src) => (
                  <span key={src.id} className="px-2 py-0.5 bg-[#0096FF]/10 border border-[#0096FF]/30 rounded text-[11px] text-[#0096FF]">
                    {src.name}
                  </span>
                ))}
              </div>
              <p className="text-[10px] text-[#555] mt-1.5">Edit those classes to change which ones roll in.</p>
            </div>
          )}

          {/* Age-group restriction applies to THIS class as a rollover destination —
              i.e., who is allowed to roll into it. Kept for backwards compatibility. */}
          {(configuringClass.rollover_from_class_ids?.length > 0 || configuringClass.rollover_from_class_id) && (
            <div>
              <label className="block text-sm font-medium text-[#FF9500] mb-2">Age Group Restriction (for inbound rollovers)</label>
              <select
                value={configuringClass.rollover_category || ''}
                onChange={(e) => {
                  const currentIds = configuringClass.rollover_from_class_ids
                    || (configuringClass.rollover_from_class_id ? [configuringClass.rollover_from_class_id] : []);
                  onSetRolloverFrom(configuringClass.id, currentIds, e.target.value);
                }}
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#FF9500] transition-colors"
                data-testid="rollover-category-select"
              >
                <option value="">Open (anyone can roll in)</option>
                <option value="YOUTH">Youth only</option>
                <option value="ADULT">Adult only</option>
                <option value="MASTERS">Masters only</option>
              </select>
            </div>
          )}
        </div>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 px-4 py-3 bg-transparent border border-[#262626] text-white rounded-md hover:border-[#0096FF] transition-colors" data-testid="cancel-division-btn">Cancel</button>
          <button onClick={onSave} className="flex-1 px-4 py-3 bg-[#0096FF] hover:bg-[#00D4FF] text-white font-semibold rounded-md transition-colors" data-testid="save-division-btn">Save Configuration</button>
        </div>
      </div>
    </div>
  );
};
